import { AblyService } from './AblyService';
import { ApiService } from './ApiService';
import type {
  WidgetConfig,
  WidgetState,
  Message,
  WidgetEvent,
  WidgetEventData
} from '@/types';

import '../styles/widget.css';

export class WebChatWidget {
  private config: WidgetConfig;
  private container: HTMLElement;
  private ablyService?: AblyService;
  private apiService: ApiService;
  private state: WidgetState;
  private clientId: string;
  private eventListeners: Map<WidgetEvent, Function[]> = new Map();
  private messagesContainer?: HTMLElement;
  private inputElement?: HTMLInputElement;
  private sendButton?: HTMLButtonElement;

  private visitorName: string | null = null;
  private visitorEmail: string | null = null;
  private clientUuid: string = '';

  conversationId: string = '';

  private isInitialized: boolean = false;

  // for multimedia
  private fileInputElement?: HTMLInputElement;
  private isDragging: boolean = false;

  //expand widget
  private isExpanded: boolean = false;

  //scrolling
  private currentPage: number = 1;
  private isLoadingMoreMessages: boolean = false;
  private hasMoreMessages: boolean = true;
  private readonly MESSAGES_PER_PAGE: number = 20;

  constructor(config: WidgetConfig) {
    this.config = {
      position: 'bottom-right',
      theme: 'light',
      serverUrl: 'https://apibusi.chatcarteldev.com/api/v1',
      channelPrefix: 'webchat',
      title: 'Chat de Soporte',
      ...config
    };

    // this.clientId = this.generateClientId();
    this.apiService = new ApiService(this.config);

    // Cargar datos guardados del cliente
    this.loadStoredClientData();

    // this.visitorId = this.getVisitorId();

    this.state = {
      isOpen: false,
      isConnected: true, // Para testing
      isLoading: false,
      messages: []
    };

    this.init();
  }

  // Cargar datos del cliente desde localStorage
  private loadStoredClientData(): void {
    try {
      const storedName = localStorage.getItem('zimpli_visitor_name');
      const storedEmail = localStorage.getItem('zimpli_visitor_email');
      const storedUuid = localStorage.getItem('zimpli_client_uuid');

      if (storedName && storedEmail && storedUuid) {
        this.visitorName = storedName;
        this.visitorEmail = storedEmail;
        this.clientUuid = storedUuid;
        this.conversationId = `conv_${storedUuid}`;

        console.log('📋 Datos del cliente restaurados:', {
          name: storedName,
          email: storedEmail,
          uuid: storedUuid
        });
      }
    } catch (error) {
      console.error('❌ Error cargando datos del cliente:', error);
    }
  }

  // Guardar datos del cliente en localStorage
  private saveClientData(name: string, email: string, clientUuid: string): void {
    try {
      localStorage.setItem('zimpli_visitor_name', name);
      localStorage.setItem('zimpli_visitor_email', email);
      localStorage.setItem('zimpli_client_uuid', clientUuid);

      this.visitorName = name;
      this.visitorEmail = email;
      this.clientUuid = clientUuid;
      this.conversationId = `conv_${clientUuid}`;

      // console.log('💾 Datos del cliente guardados:', {
      //   name,
      //   email,
      //   uuid: clientUuid
      // });
    } catch (error) {
      console.error('❌ Error guardando datos del cliente:', error);
    }
  }

  private saveApiKey(): void {
    try {
      localStorage.setItem('chatio_api_key', this.config.apiKey);
      // console.log('🔑 API key guardado en localStorage');
    } catch (error) {
      console.error('❌ Error guardando API key:', error);
    }
  }

  private getStoredApiKey(): string | null {
    try {
      return localStorage.getItem('chatio_api_key');
    } catch (error) {
      console.error('❌ Error obteniendo API key:', error);
      return null;
    }
  }

  notificationSound: HTMLAudioElement;
  private async init(): Promise<void> {
    try {
      // console.log('🚀 Inicializando WebChat Widget...');
      this.loadStyles();
      this.createContainer();
      this.createFloatingButton();

      // Inicializar servicios en background
      this.initializeServices();

      //sonido
      const soundUrl = new URL('../assets/webChatNotification.mp3', import.meta.url).href;
      this.notificationSound = new Audio(soundUrl);

      // Verificar mensajes no leídos después de inicializar
      await this.checkUnreadMessages();

      this.isInitialized = true;

      this.emitEvent('widget:initialized', { clientId: this.clientId });
    } catch (error) {
      console.error('❌ Error inicializando widget:', error);
    }
  }

  private async initializeServices(): Promise<void> {
    try {

      // Simular inicialización de servicios
      setTimeout(async () => {
        try {
          // Inicializar Ably si hay API key
          if (this.config.ablyApiKey) {
            await this.initializeAbly();
          }

          this.setState({ isConnected: true });
          // console.log('✅ Servicios conectados');
          this.updateConnectionStatus();

        } catch (error) {
          console.error('❌ Error conectando servicios:', error);
          this.setState({ isConnected: false });
        }
      }, 1000);

    } catch (error) {
      console.error('❌ Error en inicialización de servicios:', error);
    }
  }


  private async initializeAbly(): Promise<void> {
    try {
      // console.log('🔄 Intentando inicializar Ably...');

      if (!this.clientUuid) {
        console.error('❌ No se puede inicializar Ably sin clientUuid');
        return;
      }

      const ABLY_API_KEY = 'WPwNYg.xW3JUA:GoT680_XP3AVHNiSjgMsr57ISiBHlpjhV79X-ex1a_g';

      // IMPORTANTE: El canal debe coincidir con el identifier que usas en tu backend
      // Basándome en tu estructura: "identifier": "visitor_1936667776_1758765018942"
      const channelName = this.clientUuid;

      // console.log('📡 Inicializando Ably:', {
      //   channelName: channelName,
      //   clientUuid: this.clientUuid
      // });

      this.ablyService = new AblyService({
        apiKey: ABLY_API_KEY,
        clientId: this.clientUuid,
        channelName: channelName
      });

      // Configurar listeners
      this.ablyService.onMessage((message) => {
        // console.log('🎯 Mensaje procesado por el widget:', message);
        this.handleIncomingMessage(message);
      });

      this.ablyService.onConnectionStateChange((state) => {
        this.setState({ isConnected: state === 'connected' });
        this.updateConnectionStatus();
        // console.log(`🔄 Estado de conexión Ably: ${state}`);
      });

      // console.log('✅ Ably inicializado correctamente para canal:', channelName);

    } catch (error) {
      console.error('❌ Error inicializando Ably:', error);
      this.setState({ isConnected: false });
    }
  }


  private handleIncomingMessage(message: Message): void {
    // Solo procesar mensajes que no sean nuestros
    if (message.sender !== 'user') {
      // console.log('📨 Mensaje recibido del agente:', message.text);
      
      this.playNotification();
      // Agregar mensaje a la UI
      // this.addMessage(message);
      this.addMessageToUI(message, true);

      // Emitir evento para listeners externos
      this.emitEvent('message:received', { message });


      // if (!this.state.isOpen) {
      //   this.unreadCount++;
      //   this.showUnreadBadge(this.unreadCount);
      //   console.log('🔔 Nuevo mensaje no leído');
      // }
      if (this.state.isOpen) {
        // console.log('✅ Widget abierto, marcando mensaje como leído');
        this.markMessagesAsRead();
      } else {
        // Si está cerrado, incrementar contador y mostrar badge
        this.unreadCount++;
        this.showUnreadBadge(this.unreadCount);
        // console.log('🔔 Nuevo mensaje no leído');
      }
    }
  }
   playNotification() {
    this.notificationSound.currentTime = 0;
    this.notificationSound.play().catch(err => console.warn('🔇', err));
  }

  private showNotification(text: string): void {
    // Crear notificación visual en el botón flotante
    const button = this.container.querySelector('.webchat-toggle-btn');
    if (button) {
      // Agregar clase de notificación
      button.classList.add('has-notification');

      // Crear badge de notificación
      let badge = button.querySelector('.notification-badge');
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'notification-badge';
        badge.textContent = '!';
        button.appendChild(badge);
      }

      // Opcional: vibrar el botón
      // button.style.animation = 'bounce 0.6s ease-in-out';
      // setTimeout(() => {
      //   button.style.animation = '';
      // }, 600);
    }

    // Limpiar notificación cuando se abra el widget
    if (this.state.isOpen) {
      this.clearNotification();
    }
  }
  private clearNotification(): void {
    const button = this.container.querySelector('.webchat-toggle-btn');
    if (button) {
      button.classList.remove('has-notification');
      const badge = button.querySelector('.notification-badge');
      if (badge) {
        badge.remove();
      }
    }
  }

  private updateConnectionStatus(): void {
    const statusElement = this.container.querySelector('.webchat-status');
    if (statusElement) {
      if (this.state.isConnected) {
        statusElement.textContent = 'En línea';
        statusElement.className = 'webchat-status online';
      } else {
        statusElement.textContent = 'Conectando...';
        statusElement.className = 'webchat-status connecting';
      }
    }
  }


  // ACTUALIZADO: Cargar historial de conversación
  // private async loadConversationHistory2(): Promise<void> {
  //   const STORAGE_KEY = `chatio_messages_${this.conversationId}`;

  //   try {
  //     const storedMessages = localStorage.getItem(STORAGE_KEY);
  //     if (storedMessages) {
  //       const messages = JSON.parse(storedMessages);
  //       console.log('📚 Cargando historial:', messages.length, 'mensajes');

  //       // Renderizar mensajes existentes
  //       messages.forEach((msg: any) => {
  //         this.addMessage({
  //           id: msg.id,
  //           text: msg.text,
  //           sender: msg.sender,
  //           type: msg.type,
  //           timestamp: new Date(msg.timestamp)
  //         });
  //       });
  //     } else {
  //       // Primera visita - mostrar mensaje de bienvenida
  //       this.addMessage({
  //         id: Date.now().toString(),
  //         text: '¡Hola! Bienvenido al chat de soporte. ¿En qué podemos ayudarte?',
  //         sender: 'system',
  //         type: 'system',
  //         timestamp: new Date()
  //       });
  //     }
  //   } catch (error) {
  //     console.error('❌ Error cargando historial:', error);
  //   }
  // }
  // // ACTUALIZAR loadConversationHistory - sin accountUuid
  // private async loadConversationHistory3(): Promise<void> {
  //   try {
  //     console.log('📚 Cargando historial desde backend para:', this.clientUuid);

  //     this.currentPage = 1;
  //     this.hasMoreMessages = true;
  //     this.isLoadingMoreMessages = false; // Asegurar que está en false

  //     // this.showInitialLoading();


  //     const messages = await (await this.apiService.loadWebMessages(
  //       this.clientUuid,
  //       this.currentPage,
  //       this.MESSAGES_PER_PAGE
  //     ))

  //     // this.hideInitialLoading();

  //     if (messages.length > 0) {
  //       console.log(`📚 Cargados ${messages.length} mensajes del backend`);


  //       messages.forEach((message) => {
  //         this.addMessageToUI(message, false);
  //       });

  //       if (messages.length < this.MESSAGES_PER_PAGE) {
  //         this.hasMoreMessages = false;
  //         console.log('📭 No hay más mensajes para cargar');
  //       }

  //     } else {
  //       console.log('📭 No hay mensajes previos');
  //       this.hasMoreMessages = false;
  //       this.addWelcomeMessage();
  //     }

  //     this.setupInfiniteScroll();

  //   } catch (error) {
  //     console.error('❌ Error cargando historial:', error);

  //     if (typeof error === 'object' && error !== null && 'message' in error && typeof (error as any).message === 'string' && (error as any).message.includes('API Key inválido')) {
  //       this.showApiKeyError();
  //     } else {
  //       this.addWelcomeMessage();
  //     }
  //   }
  // }

  private async loadConversationHistory(): Promise<void> {
    try {
      // console.log('📚 Cargando historial desde backend para:', this.clientUuid);

      // Resetear paginación
      this.currentPage = 1;
      this.hasMoreMessages = true;
      this.isLoadingMoreMessages = false; // Asegurar que está en false

      // Mostrar loading inicial
      this.showInitialLoading();

      const messages = await this.apiService.loadWebMessages(
        this.clientUuid,
        this.currentPage,
        this.MESSAGES_PER_PAGE
      );

      // Ocultar loading inicial
      this.hideInitialLoading();

      if (messages.length > 0) {
        // console.log(`📚 Cargados ${messages.length} mensajes del backend`);

        messages.forEach((message) => {
          this.addMessageToUI(message, false);
        });

        // Si recibimos menos mensajes que el límite, no hay más
        if (messages.length < this.MESSAGES_PER_PAGE) {
          this.hasMoreMessages = false;
          // console.log('📭 No hay más mensajes para cargar');
        }

        // IMPORTANTE: Hacer scroll al final DESPUÉS de renderizar
        // y ANTES de configurar el infinite scroll
        setTimeout(() => {
          this.scrollToBottom();

          // Configurar scroll infinito DESPUÉS de posicionar al final
          setTimeout(() => {
            this.setupInfiniteScroll();
            // console.log('♾️ Scroll infinito configurado');
          }, 300);
        }, 100);

      } else {
        // console.log('📭 No hay mensajes previos');
        this.hasMoreMessages = false;
        this.addWelcomeMessage();

        // Configurar scroll infinito aunque no haya mensajes
        setTimeout(() => {
          this.setupInfiniteScroll();
        }, 300);
      }

    } catch (error) {
      console.error('❌ Error cargando historial:', error);
      this.hideInitialLoading();
      this.addWelcomeMessage();
    }
  }

  private showInitialLoading(): void {
    if (!this.messagesContainer) return;

    const loadingElement = document.createElement('div');
    loadingElement.className = 'initial-loading';
    loadingElement.id = 'initial-loading';
    loadingElement.innerHTML = `
    <div class="loading-spinner"></div>
    <span>Cargando conversación...</span>
  `;

    this.messagesContainer.appendChild(loadingElement);
  }

  private hideInitialLoading(): void {
    if (!this.messagesContainer) return;

    const loadingElement = this.messagesContainer.querySelector('#initial-loading');
    if (loadingElement) {
      loadingElement.remove();
    }
  }



  private setupInfiniteScroll(): void {
    if (!this.messagesContainer) return;

    // console.log('♾️ Configurando scroll infinito');

    // Remover listener anterior si existe
    this.messagesContainer.removeEventListener('scroll', this.handleScroll);

    // Agregar nuevo listener
    this.messagesContainer.addEventListener('scroll', this.handleScroll.bind(this));
  }


  private handleScroll = (): void => {
    if (!this.messagesContainer) return;
    if (this.isLoadingMoreMessages) {
      // console.log('⏳ Ya se están cargando mensajes, esperando...');
      return;
    }
    if (!this.hasMoreMessages) {
      // console.log('📭 No hay más mensajes para cargar');
      return;
    }

    const scrollTop = this.messagesContainer.scrollTop;
    const threshold = 150; // Aumentar threshold a 150px

    // IMPORTANTE: Solo cargar si el scroll está realmente cerca del inicio
    // y no está en el fondo (evita carga automática al abrir)
    const scrollHeight = this.messagesContainer.scrollHeight;
    const clientHeight = this.messagesContainer.clientHeight;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;

    if (scrollTop < threshold && !isAtBottom) {
      // console.log('🔄 Usuario llegó al inicio (scrollTop:', scrollTop, '), cargando más mensajes...');
      this.loadMoreMessages();
    }
  };


  // NUEVO: Cargar más mensajes (paginación)
  private async loadMoreMessages(): Promise<void> {
    if (this.isLoadingMoreMessages) {
      // console.log('⏳ Ya hay una carga en progreso');
      return;
    }

    if (!this.hasMoreMessages) {
      // console.log('📭 No hay más mensajes');
      return;
    }

    this.isLoadingMoreMessages = true;
    // console.log('🔒 isLoadingMoreMessages = true');

    // Mostrar indicador ANTES de cargar
    this.showLoadingIndicator();

    try {
      // console.log(`📄 Cargando página ${this.currentPage + 1} de mensajes`);

      const messages = await this.apiService.loadWebMessages(
        this.clientUuid,
        this.currentPage + 1,
        this.MESSAGES_PER_PAGE
      );

      if (messages.length > 0) {
        // console.log(`✅ Recibidos ${messages.length} mensajes de la página ${this.currentPage + 1}`);

        // Guardar posición actual del scroll
        const scrollHeightBefore = this.messagesContainer!.scrollHeight;
        const scrollTopBefore = this.messagesContainer!.scrollTop;

        // Agregar mensajes al inicio
        this.prependMessages(messages);

        this.currentPage++;
        // console.log(`📄 Página actual: ${this.currentPage}`);

        // Restaurar posición del scroll
        setTimeout(() => {
          this.restoreScrollPosition(scrollHeightBefore);
        }, 50);

        // Si recibimos menos mensajes que el límite, no hay más
        if (messages.length < this.MESSAGES_PER_PAGE) {
          this.hasMoreMessages = false;
          // console.log('📭 No hay más mensajes para cargar (recibidos menos del límite)');

          // Mostrar indicador solo si llegamos al final
          setTimeout(() => {
            this.showNoMoreMessagesIndicator();
          }, 100);
        }
      } else {
        this.hasMoreMessages = false;
        // console.log('📭 No hay más mensajes (respuesta vacía)');

        setTimeout(() => {
          this.showNoMoreMessagesIndicator();
        }, 100);
      }

    } catch (error) {
      console.error('❌ Error cargando más mensajes:', error);
    } finally {
      // IMPORTANTE: Ocultar loading y liberar flag DESPUÉS de todo
      setTimeout(() => {
        this.hideLoadingIndicator();
        this.isLoadingMoreMessages = false;
        // console.log('🔓 isLoadingMoreMessages = false');
      }, 200);
    }
  }


  // NUEVO: Agregar mensajes al inicio del contenedor
  private prependMessages(messages: Message[]): void {
    if (!this.messagesContainer) return;

    // Agregar al state
    messages.forEach(message => {
      const exists = this.state.messages.some(m => m.id === message.id);
      if (!exists) {
        this.state.messages.unshift(message);
      }
    });

    // Crear un fragmento para agregar todos los mensajes de una vez
    const fragment = document.createDocumentFragment();

    messages.forEach((message) => {
      const messageElement = this.createMessageElement(message);
      fragment.appendChild(messageElement);
    });

    // Insertar al inicio del contenedor
    const firstMessage = this.messagesContainer.firstChild;
    if (firstMessage) {
      this.messagesContainer.insertBefore(fragment, firstMessage);
    } else {
      this.messagesContainer.appendChild(fragment);
    }
  }

  // NUEVO: Crear elemento de mensaje (extraído de addMessageToUI)
  private createMessageElement(message: Message): HTMLElement {
    const messageElement = document.createElement('div');
    messageElement.className = `webchat-message ${message.sender}`;
    messageElement.setAttribute('data-message-id', message.id);

    // Agregar ícono si es bot o agente
    if (message.senderType && (message.sender === 'bot' || message.sender === 'agent')) {
      const iconElement = document.createElement('div');
      iconElement.className = 'sender-icon';

      if (message.senderType === 'Bot') {
        iconElement.innerHTML = `
          <svg width="17" height="21" viewBox="0 0 17 21" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0.582031 12.1152L12.457 0.865234L10.1133 8.59961L16.832 9.61523L4.95703 20.8652L7.26172 13.1699L0.582031 12.1152Z" fill="#FFC421"/>
          </svg>
        `;
      } else if (message.senderType === 'Operador') {
        iconElement.innerHTML = `
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
            <circle cx="12" cy="7" r="4"></circle>
          </svg>
        `;
      }

      messageElement.appendChild(iconElement);
    }

    const contentElement = document.createElement('div');
    contentElement.className = 'webchat-message-content';

    // Aquí va toda la lógica de renderizado según el tipo de mensaje
    // (button, list, transfer, finished, image, file, text)
    // ... usar el mismo código que tienes en addMessageToUI ...

    // console.log('asi estoy recibiendo: ', message);


    if (message.type === 'button' && message.botData) {
      // console.log('🤖 Renderizando mensaje BUTTON del bot');

      const botMessageElement = document.createElement('div');
      botMessageElement.className = 'bot-message button-message';

      let headerHTML = '';
      if (message.botData.header) {
        headerHTML = `<div class="bot-header">${message.botData.header}</div>`;
      }

      let footerHTML = '';
      if (message.botData.footer) {
        footerHTML = `<div class="bot-footer">${message.botData.footer}</div>`;
      }

      const buttonsHTML = message.botData.buttons
        ?.map((button, index) => `
        <button class="bot-button" data-message-id="${message.id}" data-button-index="${index}">
          ${button}
        </button>
      `).join('') || '';

      botMessageElement.innerHTML = `
      ${headerHTML}
      <div class="bot-body">${message.botData.body}</div>
      ${footerHTML}
      <div class="bot-buttons">${buttonsHTML}</div>
    `;

      contentElement.appendChild(botMessageElement);

      // Agregar event listeners a los botones
      setTimeout(() => {
        const buttons = botMessageElement.querySelectorAll('.bot-button');
        buttons.forEach((btn) => {
          btn.addEventListener('click', (e) => {
            const target = e.target as HTMLElement;
            const buttonText = target.textContent?.trim() || '';
            this.handleBotButtonClick(buttonText);
          });
        });
      }, 100);
    }

    else if (message.type === 'list' && message.botData) {
      // console.log('🤖 Renderizando mensaje LIST del bot');

      const botMessageElement = document.createElement('div');
      botMessageElement.className = 'bot-message list-message';

      let headerHTML = '';
      if (message.botData.header) {
        headerHTML = `<div class="bot-header">${message.botData.header}</div>`;
      }

      let footerHTML = '';
      if (message.botData.footer) {
        footerHTML = `<div class="bot-footer">${message.botData.footer}</div>`;
      }

      const listItemsHTML = message.botData.buttons
        ?.map((item, index) => `
        <button class="bot-list-item" data-message-id="${message.id}" data-item-index="${index}">
          <span class="list-item-text">${item}</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      `).join('') || '';

      botMessageElement.innerHTML = `
      ${headerHTML}
      <div class="bot-body">${message.botData.body}</div>
      ${footerHTML}
      <div class="bot-list">${listItemsHTML}</div>
    `;

      contentElement.appendChild(botMessageElement);

      // Agregar event listeners a los items de la lista
      setTimeout(() => {
        const listItems = botMessageElement.querySelectorAll('.bot-list-item');
        listItems.forEach((item) => {
          item.addEventListener('click', (e) => {
            const target = e.currentTarget as HTMLElement;
            const itemText = target.querySelector('.list-item-text')?.textContent?.trim() || '';
            this.handleBotButtonClick(itemText);
          });
        });
      }, 100);
    }
    else if (message.type === 'transfer') {
      // console.log('🔄 Renderizando mensaje de transferencia');

      const transferElement = document.createElement('div');
      transferElement.className = 'system-message';
      transferElement.innerHTML = `
      <div class="system-content">
      <div class="system-content2">
      <svg class="system-icon-chat" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
  <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16h13M4 16l4-4m-4 4 4 4M20 8H7m13 0-4 4m4-4-4-4"/>
</svg>

        <div class="system-text">${message.text}</div>
         </div>
        <div class="system-time">${this.formatTime(message.timestamp)}</div>
      </div>
    `;
      contentElement.appendChild(transferElement);

    }
    // NUEVO: Renderizar mensajes de finalización
    else if (message.type === 'finished') {
      // console.log('✅ Renderizando mensaje de finalización');

      const finishedElement = document.createElement('div');
      finishedElement.className = 'system-message';
      finishedElement.innerHTML = `
      <div class="system-content">
      <div class="system-content2">
      <svg class="system-icon-chat" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
  <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m15 9-6 6m0-6 6 6m6-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
</svg>


        <div class="system-text">${message.text}</div>
        </div>
        <div class="system-time">${this.formatTime(message.timestamp)}</div>
      </div>
    `;
      contentElement.appendChild(finishedElement);

    }

    // Manejar mensajes con imágenes
    else if (message.type === 'image' && message.fileUrl) {
      // console.log('🖼️ Renderizando imagen:', message.fileUrl);

      const imageContainer = document.createElement('div');
      imageContainer.className = 'message-image-container';

      const imageElement = document.createElement('img');
      imageElement.src = message.fileUrl;
      imageElement.className = 'message-image';
      imageElement.alt = message.fileName || 'Imagen';
      imageElement.loading = 'lazy';

      // Agregar click para abrir lightbox
      imageElement.addEventListener('click', () => {
        this.openImageLightbox(message.fileUrl!);
      });

      // Manejo de errores de carga
      imageElement.addEventListener('error', () => {
        console.error('❌ Error cargando imagen:', message.fileUrl);
        imageElement.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><rect fill="%23f0f0f0" width="200" height="200"/><text x="50%" y="50%" text-anchor="middle" fill="%23999" font-size="14">Imagen no disponible</text></svg>';
      });

      imageContainer.appendChild(imageElement);
      contentElement.appendChild(imageContainer);

      // Agregar caption si existe
      if (message.caption) {
        const captionElement = document.createElement('div');
        captionElement.className = 'image-caption';
        captionElement.textContent = message.caption;
        contentElement.appendChild(captionElement);
      }

    } else if (message.type === 'file' && message.fileUrl) {
      // console.log('📄 Renderizando documento:', message.fileUrl);

      const fileElement = document.createElement('a');
      fileElement.href = message.fileUrl;
      fileElement.target = '_blank';
      fileElement.rel = 'noopener noreferrer';
      fileElement.className = 'message-file';

      // Determinar extensión del archivo
      const ext = this.getFileExtension(message.fileName || message.fileUrl);

      fileElement.innerHTML = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
        </svg>
        <div class="message-file-info">
          <span class="message-file-name">${message.fileName || 'Documento'}</span>
          <span class="message-file-ext">${ext}</span>
        </div>
      `;
      contentElement.appendChild(fileElement);

      // Agregar caption si existe
      if (message.caption) {
        const captionElement = document.createElement('div');
        captionElement.className = 'file-caption';
        captionElement.textContent = message.caption;
        contentElement.appendChild(captionElement);
      }

    } else {
      // Mensaje de texto normal
      const textElement = document.createElement('div');
      textElement.className = 'webchat-message-text';
      textElement.textContent = message.text;
      contentElement.appendChild(textElement);
    }

    // Timestamp
    if (message.sender !== 'system') {
      const timeElement = document.createElement('div');
      timeElement.className = 'webchat-message-time';
      timeElement.textContent = this.formatTime(message.timestamp);
      contentElement.appendChild(timeElement);
    }

    messageElement.appendChild(contentElement);
    return messageElement;
  }

  // NUEVO: Restaurar posición del scroll después de agregar mensajes al inicio
  private restoreScrollPosition(previousScrollHeight: number): void {
    if (!this.messagesContainer) return;

    const newScrollHeight = this.messagesContainer.scrollHeight;
    const scrollDifference = newScrollHeight - previousScrollHeight;

    // Ajustar el scroll para mantener la posición visual
    this.messagesContainer.scrollTop = scrollDifference;
  }


  private showLoadingIndicator(): void {
    if (!this.messagesContainer) return;

    // Remover indicador anterior si existe
    this.hideLoadingIndicator();

    // console.log('🔄 Mostrando indicador de carga');

    const indicator = document.createElement('div');
    indicator.className = 'loading-more-indicator';
    indicator.id = 'loading-more-indicator'; // Agregar ID para fácil búsqueda
    indicator.innerHTML = `
    <div class="loading-spinner"></div>
    <span>Cargando mensajes anteriores...</span>
  `;

    // Insertar al inicio del contenedor
    this.messagesContainer.insertBefore(indicator, this.messagesContainer.firstChild);

    // console.log('✅ Indicador de carga mostrado');
  }

  private hideLoadingIndicator(): void {
    if (!this.messagesContainer) return;

    const indicator = this.messagesContainer.querySelector('#loading-more-indicator');
    if (indicator) {
      // console.log('✅ Ocultando indicador de carga');
      indicator.remove();
    }
  }

  private showNoMoreMessagesIndicator(): void {
    if (!this.messagesContainer) return;

    const indicator = document.createElement('div');
    indicator.className = 'no-more-messages-indicator';
    indicator.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="20 6 9 17 4 12"></polyline>
      </svg>
      <span>Inicio de la conversación</span>
    `;

    this.messagesContainer.insertBefore(indicator, this.messagesContainer.firstChild);

    // Remover después de 3 segundos
    setTimeout(() => {
      indicator.remove();
    }, 3000);
  }


  private addWelcomeMessage(): void {
    this.addMessageToUI({
      id: Date.now().toString(),
      text: '¡Hola! Bienvenido 👋. ¿En qué podemos ayudarte?',
      sender: 'system',
      type: 'system',
      timestamp: new Date()
    }, false);
  }

  private addMessageToUI(message: Message, isNewMessage: boolean = true): void {
    if (!this.messagesContainer) return;

    this.state.messages.push(message);

    const messageElement = document.createElement('div');
    messageElement.className = `webchat-message ${message.sender}`;
    messageElement.setAttribute('data-message-id', message.id);

    if (message.senderType && (message.sender === 'bot' || message.sender === 'agent')) {
      const iconElement = document.createElement('div');
      iconElement.className = 'sender-icon';

      if (message.senderType === 'Bot') {
        iconElement.innerHTML = `
        <svg width="16" height="20" viewBox="0 0 17 21" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0.582031 12.1152L12.457 0.865234L10.1133 8.59961L16.832 9.61523L4.95703 20.8652L7.26172 13.1699L0.582031 12.1152Z" fill="#FFC421"/>
        </svg>
      `;
      } else if (message.senderType === 'Operador') {
        iconElement.innerHTML = `
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
          <circle cx="12" cy="7" r="4"></circle>
        </svg>
      `;
      }

      messageElement.appendChild(iconElement);
    }


    const contentElement = document.createElement('div');
    contentElement.className = 'webchat-message-content';

    const contentElementButtons = document.createElement('div');
    contentElementButtons.className = 'webchat-message-content-buttons';

    console.log('asi estoy recibiendo: ', message);


    if (message.type === 'button' && message.botData) {
      console.log('🤖 Renderizando mensaje BUTTON del bot');

      const botMessageElement = document.createElement('div');
      botMessageElement.className = 'bot-message button-message';

      let headerHTML = '';
      if (typeof message.botData.header === 'object' && message.botData.header?.type === 'image') {
        headerHTML = `<div class="bot-header"><img src="${message.botData.header?.link}" alt="Header Image" class="bot-header-image"/></div>`;
      } else {
        headerHTML = `<div class="bot-header">${message.botData.header}</div>`;
      }

      let footerHTML = '';
      if (message.botData.footer) {
        footerHTML = `<div class="bot-footer">${message.botData.footer}</div>`;
      }

      const buttonsHTML = message.botData.buttons
        ?.map((button, index) => `
        <button class="bot-button" data-message-id="${message.id}" data-button-index="${index}">
          ${button}
        </button>
      `).join('') || '';

      botMessageElement.innerHTML = `
      ${headerHTML}
      <div class="bot-body">${message.botData.body}</div>
      ${footerHTML}
      <div class="bot-buttons">${buttonsHTML}</div>
    `;

      contentElementButtons.appendChild(botMessageElement);

      setTimeout(() => {
        const headerImg = botMessageElement.querySelector('.bot-header-image') as HTMLImageElement;
        if (headerImg) {
          headerImg.addEventListener('click', () => {
            this.openImageLightbox(headerImg.src);
          });
        }
      }, 0);

      // Agregar event listeners a los botones
      setTimeout(() => {
        const buttons = botMessageElement.querySelectorAll('.bot-button');
        buttons.forEach((btn) => {
          btn.addEventListener('click', (e) => {
            const target = e.target as HTMLElement;
            const buttonText = target.textContent?.trim() || '';
            this.handleBotButtonClick(buttonText);
          });
        });
      }, 100);
    } else if (message.type === 'list' && message.botData) {
      // console.log('🤖 Renderizando mensaje LIST del bot');

      const botMessageElement = document.createElement('div');
      botMessageElement.className = 'bot-message list-message';

      let headerHTML = '';
      if (message.botData.header) {
        headerHTML = `<div class="bot-header">${message.botData.header}</div>`;
      }

      let footerHTML = '';
      if (message.botData.footer) {
        footerHTML = `<div class="bot-footer">${message.botData.footer}</div>`;
      }

      const listItemsHTML = message.botData.buttons
        ?.map((item, index) => `
        <button class="bot-list-item" data-message-id="${message.id}" data-item-index="${index}">
          <span class="list-item-text">${item}</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      `).join('') || '';

      botMessageElement.innerHTML = `
      ${headerHTML}
      <div class="bot-body">${message.botData.body}</div>
      ${footerHTML}
      <div class="bot-list">${listItemsHTML}</div>
    `;

      contentElementButtons.appendChild(botMessageElement);

      // Agregar event listeners a los items de la lista
      setTimeout(() => {
        const listItems = botMessageElement.querySelectorAll('.bot-list-item');
        listItems.forEach((item) => {
          item.addEventListener('click', (e) => {
            const target = e.currentTarget as HTMLElement;
            const itemText = target.querySelector('.list-item-text')?.textContent?.trim() || '';
            this.handleBotButtonClick(itemText);
          });
        });
      }, 100);
    } else if (message.type === 'transfer') {
      // console.log('🔄 Renderizando mensaje de transferencia');

      const transferElement = document.createElement('div');
      transferElement.className = 'system-message';
      transferElement.innerHTML = `
      <div class="system-content">
      <div class="system-content2">
      <svg class="system-icon-chat" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
  <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16h13M4 16l4-4m-4 4 4 4M20 8H7m13 0-4 4m4-4-4-4"/>
</svg>

        <div class="system-text">${message.text}</div>
         </div>
        <div class="system-time">${this.formatTime(message.timestamp)}</div>
      </div>
    `;
      contentElement.appendChild(transferElement);

    } else if (message.type === 'finished') {
      // console.log('✅ Renderizando mensaje de finalización');

      const finishedElement = document.createElement('div');
      finishedElement.className = 'system-message';
      finishedElement.innerHTML = `
      <div class="system-content">
      <div class="system-content2">
      <svg class="system-icon-chat" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
  <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m15 9-6 6m0-6 6 6m6-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
</svg>


        <div class="system-text">${message.text}</div>
        </div>
        <div class="system-time">${this.formatTime(message.timestamp)}</div>
      </div>
    `;
      contentElement.appendChild(finishedElement);

    } else if (message.type === 'image' && message.fileUrl) {
      // console.log('🖼️ Renderizando imagen:', message.fileUrl);

      const imageContainer = document.createElement('div');
      imageContainer.className = 'message-image-container';

      const imageElement = document.createElement('img');
      imageElement.src = message.fileUrl;
      imageElement.className = 'message-image';
      imageElement.alt = message.fileName || 'Imagen';
      imageElement.loading = 'lazy';

      imageElement.addEventListener('click', () => {
        this.openImageLightbox(message.fileUrl!);
      });

      // Manejo de errores de carga
      imageElement.addEventListener('error', () => {
        console.error('❌ Error cargando imagen:', message.fileUrl);
        imageElement.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><rect fill="%23f0f0f0" width="200" height="200"/><text x="50%" y="50%" text-anchor="middle" fill="%23999" font-size="14">Imagen no disponible</text></svg>';
      });

      imageContainer.appendChild(imageElement);
      contentElement.appendChild(imageContainer);

      // Agregar caption si existe
      if (message.caption) {
        const captionElement = document.createElement('div');
        captionElement.className = 'image-caption';
        captionElement.textContent = message.caption;
        contentElement.appendChild(captionElement);
      }

    } else if (message.type === 'file' && message.fileUrl) {
      // console.log('📄 Renderizando documento:', message.fileUrl);

      const fileElement = document.createElement('a');
      fileElement.href = message.fileUrl;
      fileElement.target = '_blank';
      fileElement.rel = 'noopener noreferrer';
      fileElement.className = 'message-file';

      // Determinar extensión del archivo
      const ext = this.getFileExtension(message.fileName || message.fileUrl);

      fileElement.innerHTML = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
        </svg>
        <div class="message-file-info">
          <span class="message-file-name">${message.fileName || 'Documento'}</span>
          <span class="message-file-ext">${ext}</span>
        </div>
      `;
      contentElement.appendChild(fileElement);

      // Agregar caption si existe
      if (message.caption) {
        const captionElement = document.createElement('div');
        captionElement.className = 'file-caption';
        captionElement.textContent = message.caption;
        contentElement.appendChild(captionElement);
      }

    } else {
      console.log('Agregando mensaje de texto normal');
      const textElement = document.createElement('div');
      textElement.className = 'webchat-message-text';
      textElement.textContent = message.text;
      contentElement.appendChild(textElement);
    }

    // Timestamp
    // if (message.sender !== 'system') {
    //   const timeElement = document.createElement('div');
    //   timeElement.className = 'webchat-message-time';
    //   timeElement.textContent = this.formatTime(message.timestamp);
    //   message.type === 'button' || message.type === 'list' ? contentElementButtons.appendChild(timeElement) : contentElement.appendChild(timeElement);
    // }

    //AREA TIME
    const timeElement = document.createElement('div');

    if (message.sender !== 'system' && !(message.type === 'button' || message.type === 'list')) {
      console.log('Adding time element outside bubble');
      timeElement.className = 'webchat-message-time';
      timeElement.textContent = this.formatTime(message.timestamp);

      const messageWrapper = document.createElement('div');

      message.sender === 'user' ? messageWrapper.className = 'message-wrapper user' : messageWrapper.className = 'message-wrapper';

      // message.sender === 'user' ? messageWrapper.classList.add('user') : messageWrapper.classList.add('bot-message-wrapper');

      // Añadimos el mensaje y luego el tiempo fuera del globo
      messageWrapper.appendChild(contentElement);
      messageWrapper.appendChild(timeElement);

      messageElement.appendChild(messageWrapper);
    } else if (message.type === 'button' || message.type === 'list') {
      timeElement.className = 'webchat-message-time';
      timeElement.textContent = this.formatTime(message.timestamp);
      contentElementButtons.appendChild(timeElement);
      messageElement.appendChild(contentElementButtons)
    }
    else {
      // En caso de mensajes del sistema, mantener igual
      messageElement.appendChild(contentElement);
    }



    // message.type === 'button' || message.type === 'list' ? messageElement.appendChild(contentElementButtons) : messageElement.appendChild(contentElement);


    this.messagesContainer.appendChild(messageElement);


    this.scrollToBottom();

  }


  // NUEVO: Manejar click en botones del bot
  private handleBotButtonClick(buttonText: string): void {
    // console.log('🤖 Botón del bot clickeado:', buttonText);

    // Enviar el texto del botón como mensaje del usuario
    if (this.inputElement) {
      this.inputElement.value = buttonText;
      this.handleSendMessage();
    }
  }




  // Abrir lightbox para ver imagen en tamaño completo
  private openImageLightbox(imageUrl: string): void {
    // console.log('🔍 Abriendo lightbox para imagen:', imageUrl);

    // Crear lightbox
    const lightbox = document.createElement('div');
    lightbox.className = 'image-lightbox';

    lightbox.innerHTML = `
      <div class="lightbox-content">

<div class="button-container_imageprev">

  <button class="button_imageprev" id="download-btn">
    <svg class="icon" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
  <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 13V4M7 14H5a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4a1 1 0 0 0-1-1h-2m-1-5-4 5-4-5m9 8h.01"/>
</svg>

  </button>

  <button class="button_imageprev lightbox-close">
 <svg class="icon" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
  <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18 17.94 6M18 18 6.06 6"/>
</svg>

  </button>
</div>

        <img src="${imageUrl}" class="lightbox-image" alt="Vista completa">
      </div>
    `;

    document.body.appendChild(lightbox);

    // Prevenir scroll del body
    document.body.style.overflow = 'hidden';

    // Cerrar al hacer click en el overlay o botón
    const closeLightbox = () => {
      lightbox.remove();
      document.body.style.overflow = '';
    };

    lightbox.addEventListener('click', (e) => {
      if (e.target === lightbox) {
        closeLightbox();
      }
    });

    const closeButton = lightbox.querySelector('.lightbox-close');
    if (closeButton) {
      closeButton.addEventListener('click', closeLightbox);
    }

    // Cerrar con tecla ESC
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeLightbox();
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('keydown', handleEscape);

    // Manejo de errores de carga
    const lightboxImage = lightbox.querySelector('.lightbox-image') as HTMLImageElement;
    if (lightboxImage) {
      lightboxImage.addEventListener('error', () => {
        console.error('❌ Error cargando imagen en lightbox');
        closeLightbox();
        this.showFileError('No se pudo cargar la imagen');
      });
    }

    const downloadBtn = lightbox.querySelector('#download-btn');
    if (downloadBtn) {
      downloadBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const link = document.createElement('a');
        link.href = imageUrl;
        link.download = imageUrl.split('/').pop() || 'imagen_descargada.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
    }

  }


  // Método helper para logging de mensajes multimedia
  private logMediaMessage(message: Message): void {
    const icon = message.type === 'image' ? '🖼️' : '📄';
    // console.log(`${icon} Mensaje multimedia:`, {
    //   type: message.type,
    //   fileUrl: message.fileUrl,
    //   fileName: message.fileName,
    //   caption: message.caption,
    //   sender: message.sender
    // });
  }

  // Obtener extensión del archivo
  private getFileExtension(filename: string): string {
    try {
      const parts = filename.split('.');
      if (parts.length > 1) {
        return parts[parts.length - 1].toUpperCase();
      }
      return 'FILE';
    } catch (e) {
      return 'FILE';
    }
  }


  // Método para procesar mensajes entrantes de Ably con archivos
  private processIncomingMessageData(messageData: any): Message {
    const message: Message = {
      id: messageData.ID || Date.now().toString(),
      text: messageData.Caption || messageData.Phrase || '',
      sender: messageData.Class === 'reverse_agent' ? 'agent' : 'user',
      type: this.determineMessageType(messageData.Type),
      timestamp: new Date(messageData.Date || Date.now()),
    };

    // Agregar datos de archivo si es imagen o documento
    if (messageData.Type === 'IMAGE' || messageData.Type === 'DOCUMENT') {
      message.fileUrl = messageData.Phrase; // La URL está en Phrase
      message.caption = messageData.Caption;
    }

    return message;
  }

  private determineMessageType(type: string): 'text' | 'image' | 'file' | 'system' {
    if (type === 'IMAGE') return 'image';
    if (type === 'DOCUMENT') return 'file';
    return 'text';
  }







  private loadStyles(): void {
    if (document.getElementById('webchat-widget-styles')) {
      return;
    }

    const link = document.createElement('link');
    link.id = 'webchat-widget-styles';
    link.rel = 'stylesheet';

    // 👇 Esto le dice a Vite que procese el CSS y devuelva la URL correcta en runtime
    link.href = new URL('../styles/widget.css', import.meta.url).href;

    document.head.appendChild(link);
  }

  private createContainer(): void {
    this.container = document.createElement('div');
    this.container.id = 'webchat-widget-container';
    this.container.className = `webchat-widget ${this.config.position} ${this.config.theme}`;

    if (this.config.primaryColor) {
      this.container.style.setProperty('--webchat-primary', this.config.primaryColor);
    }

    document.body.appendChild(this.container);
  }

  toggleButton: HTMLButtonElement | null = null;
  private createFloatingButton(): void {
    const button = document.createElement('button');
    button.className = 'webchat-toggle-btn';
    button.innerHTML = this.getButtonIcon(false);
    button.addEventListener('click', () => this.toggle());
    button.setAttribute('aria-label', 'Abrir chat de soporte');

    this.container.appendChild(button);
    this.toggleButton = button;
  }

  private async toggle(): Promise<void> {
    if (this.state.isOpen) {
      this.close();
      this.animateButtonIcon(false);
    } else {
      this.open();
      this.animateButtonIcon(true);
    }
  }

  private animateButtonIcon(isOpen: boolean): void {
    if (!this.toggleButton) return;

    const svg = this.toggleButton.querySelector('svg');
    if (!svg) return;

    // Aplica clase de rotación
    this.toggleButton.classList.add('animating');

    // Espera la mitad del tiempo de rotación antes de cambiar el icono
    setTimeout(() => {
      if (this.toggleButton) {
        this.toggleButton.innerHTML = this.getButtonIcon(isOpen);
      }
    }, 150); // 150ms = mitad de la animación


    setTimeout(() => {
      if (this.toggleButton) {
        this.toggleButton.classList.remove('animating');
      }
    }, 300);
  }





  private async open(): Promise<void> {
    if (this.state.isOpen) return;

    // console.log('📂 Abriendo widget...');
    this.setState({ isOpen: true });
    this.container.classList.add('open');

    // this.clearNotification();
    this.hideUnreadBadge();

    // Verificar si ya tenemos nombre del visitante
    // const savedName = localStorage.getItem('zimpli_visitor_name');
    // if (savedName && !this.visitorName) {
    //   // Restaurar nombre guardado
    //   this.visitorName = savedName;
    //   this.conversationId = `conv_${this.visitorId}`;
    //   console.log('📋 Nombre restaurado desde localStorage:', savedName);
    // }

    if (!this.clientUuid || !this.visitorName || !this.visitorEmail) {
      // Mostrar formulario de nombre
      this.createNameForm();
    } else {
      // Mostrar chat directamente
      this.createChatWindow();
      await this.loadConversationHistory();
      // Marcar mensajes como leídos
      // await this.markMessagesAsRead();

      if (this.clientUuid) {
        await this.markMessagesAsRead();
      }

      // if (!this.ablyService) {
      //   await this.initializeAbly();
      // }

      if (!this.ablyService && this.config.ablyApiKey) {
        await this.initializeAbly();
      }
    }

    this.emitEvent('widget:opened');
  }

  private close(): void {
    if (!this.state.isOpen) return;

    // console.log('📁 Cerrando widget...');
    this.setState({ isOpen: false });
    this.container.classList.remove('open');

    this.emitEvent('widget:closed');
  }


  private createChatWindow(): void {
    const existingWindow = this.container.querySelector('.webchat-window');
    if (existingWindow) {
      existingWindow.remove();
    }

    const chatWindow = document.createElement('div');
    chatWindow.className = 'webchat-window';

    chatWindow.innerHTML = `
      <div class="webchat-header">
        <div class="webchat-header-info">
          <div class="webchat-title">${this.config.title || 'Chat'}</div>
          <div class="webchat-status">En línea</div>
        </div>
          <div class="webchat-header-actions">
          <button class="webchat-expand-btn" id="expand-btn" aria-label="Expandir chat" title="Expandir">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="15 3 21 3 21 9"></polyline>
              <polyline points="9 21 3 21 3 15"></polyline>
              <line x1="21" y1="3" x2="14" y2="10"></line>
              <line x1="3" y1="21" x2="10" y2="14"></line>
            </svg>
          </button>
          <button class="webchat-close-btn" aria-label="Cerrar chat">
            <svg  width="18" height="18" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
  <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18 17.94 6M18 18 6.06 6"/>
</svg>

          </button>
        </div>
      </div>
      <div class="webchat-messages" id="webchat-messages-container"></div>
      <div class="webchat-drag-overlay" id="drag-overlay">
        <div class="drag-content">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="17 8 12 3 7 8"></polyline>
            <line x1="12" y1="3" x2="12" y2="15"></line>
          </svg>
          <p>Suelta el archivo aquí</p>
        </div>
      </div>
      <div class="webchat-input-container">
        <div class="webchat-input-wrapper">
          <button class="webchat-attach-btn" id="webchat-attach-button" title="Adjuntar archivo">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
            </svg>
          </button>
          <input type="text" class="webchat-input" placeholder="Escribe tu mensaje..." id="webchat-input-field">
          <input type="file" id="webchat-file-input" accept="image/png,image/jpeg,image/jpg,application/pdf" style="display: none;">
          <button class="webchat-send-btn" id="webchat-send-button">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
            </svg>
          </button>
        </div>
      </div>
    `;

    this.container.appendChild(chatWindow);

    // Obtener referencias a elementos
    this.messagesContainer = chatWindow.querySelector('#webchat-messages-container') as HTMLElement;
    this.inputElement = chatWindow.querySelector('#webchat-input-field') as HTMLInputElement;
    this.sendButton = chatWindow.querySelector('#webchat-send-button') as HTMLButtonElement;
    this.fileInputElement = chatWindow.querySelector('#webchat-file-input') as HTMLInputElement;

    // Configurar event listeners
    this.setupEventListeners();
    this.setupFileUpload();
    this.setupDragAndDrop();
    this.setupExpandButton();

    // Agregar mensaje de bienvenida
    // this.addMessage({
    //   id: Date.now().toString(),
    //   text: '¡Hola! Bienvenido al chat de soporte. ¿En qué podemos ayudarte?',
    //   sender: 'system',
    //   type: 'system',
    //   timestamp: new Date()
    // });

    // Enfocar el input
    setTimeout(() => {
      if (this.inputElement) {
        this.inputElement.focus();
      }
    }, 100);
  }

  private setupExpandButton(): void {
    const expandBtn = this.container.querySelector('#expand-btn');

    if (expandBtn) {
      expandBtn.addEventListener('click', () => {
        this.toggleExpand();
      });
    }
  }

  private toggleExpand(): void {
    const chatWindow = this.container.querySelector('.webchat-window');
    const expandBtn = this.container.querySelector('#expand-btn');

    if (!chatWindow || !expandBtn) return;

    this.isExpanded = !this.isExpanded;

    if (this.isExpanded) {
      chatWindow.classList.add('expanded');
      expandBtn.setAttribute('title', 'Contraer');
      expandBtn.innerHTML = `
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="4 14 10 14 10 20"></polyline>
          <polyline points="20 10 14 10 14 4"></polyline>
          <line x1="14" y1="10" x2="21" y2="3"></line>
          <line x1="3" y1="21" x2="10" y2="14"></line>
        </svg>
      `;

    } else {
      chatWindow.classList.remove('expanded');
      expandBtn.setAttribute('title', 'Expandir');
      expandBtn.innerHTML = `
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="15 3 21 3 21 9"></polyline>
          <polyline points="9 21 3 21 3 15"></polyline>
          <line x1="21" y1="3" x2="14" y2="10"></line>
          <line x1="3" y1="21" x2="10" y2="14"></line>
        </svg>
      `;
    }

    // Scroll al final después de cambiar tamaño
    // setTimeout(() => {
    //   this.scrollToBottom();
    // }, 300);
  }


  public expandWidget(): void {
    if (!this.isExpanded) {
      this.toggleExpand();
    }
  }

  public contractWidget(): void {
    if (this.isExpanded) {
      this.toggleExpand();
    }
  }





  // Configurar sistema de carga de archivos
  private setupFileUpload(): void {
    const attachButton = this.container.querySelector('#webchat-attach-button');

    if (attachButton && this.fileInputElement) {
      // Abrir selector de archivos al hacer click
      attachButton.addEventListener('click', () => {
        this.fileInputElement?.click();
      });

      // Manejar selección de archivo
      this.fileInputElement.addEventListener('change', (e) => {
        const target = e.target as HTMLInputElement;
        const file = target.files?.[0];

        if (file) {
          this.handleFileSelection(file);
          // Limpiar input para permitir seleccionar el mismo archivo de nuevo
          target.value = '';
        }
      });
    }
  }
  // Configurar drag and drop
  private setupDragAndDrop(): void {
    const messagesContainer = this.messagesContainer;
    const dragOverlay = this.container.querySelector('#drag-overlay') as HTMLElement;

    if (!messagesContainer || !dragOverlay) return;

    // Prevenir comportamiento por defecto del navegador
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      messagesContainer.addEventListener(eventName, (e) => {
        e.preventDefault();
        e.stopPropagation();
      });
    });

    // Mostrar overlay al arrastrar archivo sobre el chat
    ['dragenter', 'dragover'].forEach(eventName => {
      messagesContainer.addEventListener(eventName, () => {
        if (!this.isDragging) {
          this.isDragging = true;
          dragOverlay.classList.add('active');
        }
      });
    });

    // Ocultar overlay al salir
    ['dragleave', 'drop'].forEach(eventName => {
      messagesContainer.addEventListener(eventName, () => {
        this.isDragging = false;
        dragOverlay.classList.remove('active');
      });
    });

    // Manejar drop
    messagesContainer.addEventListener('drop', (e: DragEvent) => {
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        this.handleFileSelection(files[0]);
      }
    });
  }
  // Manejar selección de archivo
  private async handleFileSelection(file: File): Promise<void> {
    // console.log('📎 Archivo seleccionado:', file.name, file.type, file.size);

    // Validar tipo de archivo
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      this.showFileError('Tipo de archivo no soportado. Solo se permiten PNG, JPG, JPEG y PDF.');
      return;
    }

    // Validar tamaño (máximo 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      this.showFileError('El archivo es muy grande. Tamaño máximo: 10MB.');
      return;
    }

    // Previsualizar archivo
    this.showFilePreview(file);
  }


  private handlePasteEvent(event: ClipboardEvent): void {
    if (!event.clipboardData) return;

    const items = event.clipboardData.items;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          event.preventDefault();
          this.handleFileSelection(file);
        }
      }
    }
  }

  // Mostrar preview del archivo antes de enviar
  private showFilePreview(file: File): void {
    const isImage = file.type.startsWith('image/');

    // Crear preview en el área de mensajes
    const previewDiv = document.createElement('div');
    previewDiv.className = 'webchat-file-preview';

    if (isImage) {
      const reader = new FileReader();
      reader.onload = (e) => {
        previewDiv.innerHTML = `
          <div class="preview-content">
            <img src="${e.target?.result}" alt="Preview">
            <div class="preview-info">
              <span class="file-name">${file.name}</span>
              <span class="file-size">${this.formatFileSize(file.size)}</span>
            </div>
            <div class="preview-actions">
              <input type="text" class="caption-input" placeholder="Agregar comentario (opcional)" maxlength="200">
              <button class="send-file-btn">Enviar</button>
              <button class="cancel-file-btn">Cancelar</button>
            </div>
          </div>
        `;

        this.messagesContainer?.appendChild(previewDiv);
        this.setupPreviewActions(previewDiv, file);

        setTimeout(() => {
          this.scrollToBottom();
        }, 300);
        // this.scrollToBottom();
      };
      reader.readAsDataURL(file);
    } else {
      // Preview para PDF
      previewDiv.innerHTML = `
        <div class="preview-content document">
          <div class="document-icon">
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
          </div>
          <div class="preview-info">
            <span class="file-name">${file.name}</span>
            <span class="file-size">${this.formatFileSize(file.size)}</span>
          </div>
          <div class="preview-actions">
            <input type="text" class="caption-input" placeholder="Agregar comentario (opcional)" maxlength="200">
            <button class="send-file-btn">Enviar</button>
            <button class="cancel-file-btn">Cancelar</button>
          </div>
        </div>
      `;

      this.messagesContainer?.appendChild(previewDiv);
      this.setupPreviewActions(previewDiv, file);
      // this.scrollToBottom();
      setTimeout(() => {
        this.scrollToBottom();
      }, 300);
    }
  }

  // Configurar acciones del preview
  private setupPreviewActions(previewDiv: HTMLElement, file: File): void {
    const sendBtn = previewDiv.querySelector('.send-file-btn');
    const cancelBtn = previewDiv.querySelector('.cancel-file-btn');
    const captionInput = previewDiv.querySelector('.caption-input') as HTMLInputElement;

    sendBtn?.addEventListener('click', async () => {
      const caption = captionInput?.value.trim();
      await this.sendFile(file, caption);
      previewDiv.remove();
    });

    cancelBtn?.addEventListener('click', () => {
      previewDiv.remove();
    });
  }

  // Mostrar error de archivo
  private showFileError(message: string): void {
    this.addMessage({
      id: Date.now().toString(),
      text: message,
      sender: 'system',
      type: 'system',
      timestamp: new Date()
    });
  }

  // Formatear tamaño de archivo
  private formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }



  // Enviar archivo
  private async sendFile(file: File, caption?: string): Promise<void> {
    if (!this.visitorName || !this.clientUuid) {
      console.error('❌ No hay nombre de visitante');
      return;
    }

    // console.log('📤 Enviando archivo:', file.name);

    // Mostrar mensaje de "enviando"
    const loadingMessage = this.addLoadingMessage(file, caption);

    try {
      const result = await this.apiService.sendWebMessageWithFile(
        this.visitorName,
        this.clientUuid,
        file,
        caption
      );

      // Remover mensaje de loading
      loadingMessage.remove();

      // Agregar mensaje del archivo enviado
      const fileMessage: Message = {
        id: result.id || Date.now().toString(),
        text: caption || file.name,
        sender: 'user',
        type: file.type.startsWith('image/') ? 'image' : 'file',
        timestamp: new Date(),
        fileUrl: result.fileUrl || URL.createObjectURL(file),
        fileName: file.name,
        fileType: file.type,
        caption: caption
      };

      this.addMessageToUI(fileMessage, true);
      // console.log('✅ Archivo enviado correctamente');

    } catch (error) {
      console.error('❌ Error enviando archivo:', error);
      loadingMessage.remove();

      this.addMessage({
        id: Date.now().toString(),
        text: 'Error enviando archivo. Intenta de nuevo.',
        sender: 'system',
        type: 'system',
        timestamp: new Date()
      });
    }
  }
  // Agregar mensaje de loading mientras se sube
  private addLoadingMessage(file: File, caption?: string): HTMLElement {
    const loadingDiv = document.createElement('div');
    loadingDiv.className = 'webchat-message user loading';

    const isImage = file.type.startsWith('image/');
    const icon = isImage ?
      `<svg class="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
  <path fill-rule="evenodd" d="M13 10a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2H14a1 1 0 0 1-1-1Z" clip-rule="evenodd"/>
  <path fill-rule="evenodd" d="M2 6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v12c0 .556-.227 1.06-.593 1.422A.999.999 0 0 1 20.5 20H4a2.002 2.002 0 0 1-2-2V6Zm6.892 12 3.833-5.356-3.99-4.322a1 1 0 0 0-1.549.097L4 12.879V6h16v9.95l-3.257-3.619a1 1 0 0 0-1.557.088L11.2 18H8.892Z" clip-rule="evenodd"/>
</svg>` : `<svg class="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
  <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 3v4a1 1 0 0 1-1 1H5m4 8h6m-6-4h6m4-8v16a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V7.914a1 1 0 0 1 .293-.707l3.914-3.914A1 1 0 0 1 9.914 3H18a1 1 0 0 1 1 1Z"/>
</svg>
`;

    loadingDiv.innerHTML = `
      <div class="webchat-message-content">
        <div class="loading-file">
          <span class="file-icon">${icon}</span>
          <div class="loading-info">
            <div class="file-name">${file.name}</div>
            ${caption ? `<div class="file-caption">${caption}</div>` : ''}
            <div class="loading-bar">
              <div class="loading-progress"></div>
            </div>
            <div class="loading-text">Enviando...</div>
          </div>
        </div>
      </div>
    `;

    this.messagesContainer?.appendChild(loadingDiv);
    this.scrollToBottom();
    return loadingDiv;
  }




  private setupEventListeners(): void {
    // Botón de cerrar
    const closeButton = this.container.querySelector('.webchat-close-btn');
    if (closeButton) {
      closeButton.addEventListener('click', () => this.toggle());
    }

    // Input field
    if (this.inputElement) {
      this.inputElement.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          this.handleSendMessage();
        }
      });

      this.inputElement.addEventListener('input', () => {
        this.updateSendButtonState();
      });

      this.inputElement.addEventListener('paste', (event: ClipboardEvent) => {
        this.handlePasteEvent(event);
      });
    }

    // Send button
    if (this.sendButton) {
      this.sendButton.addEventListener('click', () => {
        this.handleSendMessage();
      });
    }

    // console.log('🔗 Event listeners configurados');
  }

  private updateSendButtonState(): void {
    if (!this.inputElement || !this.sendButton) return;

    const hasText = this.inputElement.value.trim().length > 0;
    this.sendButton.disabled = !hasText;
  }


  private async handleSendMessage(): Promise<void> {

    if (!this.inputElement) return;

    const messageText = this.inputElement.value.trim();
    if (!messageText) return;

    if (!this.visitorName || !this.clientUuid) {
      console.error('❌ No hay datos del visitante');
      return;
    }

    // console.log('📤 Enviando mensaje:', messageText);

    const userMessage = {
      id: Date.now().toString(),
      text: messageText,
      sender: 'user' as const,
      type: 'text' as const,
      timestamp: new Date()
    };

    this.addMessageToUI(userMessage);

    this.inputElement.value = '';
    this.updateSendButtonState();
    this.inputElement.focus();

    try {
      await this.apiService.sendWebMessage(
        this.visitorName,
        this.clientUuid,
        messageText
      );
      // console.log('✅ Mensaje enviado correctamente');

    } catch (error) {
      console.error('❌ Error enviando mensaje:', error);

      let errorMessage = 'Error enviando mensaje. Intenta de nuevo.';

      if (typeof error === 'object' && error !== null && 'message' in error && typeof (error as any).message === 'string' && (error as any).message.includes('API Key inválido')) {
        errorMessage = 'Error de autenticación. Contacta al administrador del sitio.';
      }

      this.addMessage({
        id: Date.now().toString(),
        text: errorMessage,
        sender: 'system',
        type: 'system',
        timestamp: new Date()
      });
    }
  }

  // Método simplificado para mostrar error de API key
  private showApiKeyError(): void {
    this.addMessageToUI({
      id: Date.now().toString(),
      text: 'Error de configuración. No se pueden cargar los mensajes anteriores. Contacta al administrador del sitio.',
      sender: 'system',
      type: 'system',
      timestamp: new Date()
    }, false);
  }

  // Método de debugging simplificado
  // public getDebugInfo(): any {
  //   // return {
  //   //   visitorId: this.visitorId,
  //   //   visitorName: this.visitorName,
  //   //   apiKey: this.config.apiKey ? 'Configurado' : 'No configurado',
  //   //   storedApiKey: this.getStoredApiKey() ? 'Guardado' : 'No guardado',
  //   //   ablyApiKey: this.config.ablyApiKey ? 'Configurado' : 'No configurado',
  //   //   messagesCount: this.state.messages.length,
  //   //   isConnected: this.state.isConnected,
  //   //   isInitialized: this.isInitialized
  //   // };
  //   return {
  //     clientUuid: this.clientUuid,
  //     visitorName: this.visitorName,
  //     visitorEmail: this.visitorEmail,
  //     conversationId: this.conversationId,
  //     apiKey: this.config.apiKey ? 'Configurado' : 'No configurado',
  //     storedApiKey: this.getStoredApiKey() ? 'Guardado' : 'No guardado',
  //     ablyApiKey: this.config.ablyApiKey ? 'Configurado' : 'No configurado',
  //     messagesCount: this.state.messages.length,
  //     unreadCount: this.unreadCount,
  //     isConnected: this.state.isConnected,
  //     isInitialized: this.isInitialized
  //   };
  // }


  private addMessage(message: Message): void {
    if (!this.messagesContainer) return;

    // Guardar en localStorage
    // this.saveMessageToHistory(message);

    // Agregar al estado
    this.state.messages.push(message);

    // Crear elemento del mensaje
    const messageElement = document.createElement('div');
    messageElement.className = `webchat-message ${message.sender}`;

    const contentElement = document.createElement('div');
    contentElement.className = 'webchat-message-content';

    const textElement = document.createElement('div');
    textElement.className = 'webchat-message-text';
    textElement.textContent = message.text;

    contentElement.appendChild(textElement);

    // Agregar timestamp para mensajes de usuario y agente
    if (message.sender !== 'system') {
      const timeElement = document.createElement('div');
      timeElement.className = 'webchat-message-time';
      timeElement.textContent = this.formatTime(message.timestamp);
      contentElement.appendChild(timeElement);
    }

    messageElement.appendChild(contentElement);
    this.messagesContainer.appendChild(messageElement);

    // Scroll hacia abajo
    this.scrollToBottom();

    // console.log('✅ Mensaje agregado y guardado:', message.text);
  }

  // private formatTime2(date: Date): string {
  //   return date.toLocaleTimeString('es-ES', {
  //     hour: '2-digit',
  //     minute: '2-digit'
  //   });
  // }
  private formatTime(date: Date): string {
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  }



  // private scrollToBottom(): void {
  //   if (!this.messagesContainer) return;

  //   setTimeout(() => {
  //     this.messagesContainer!.scrollTop = this.messagesContainer!.scrollHeight;
  //   }, 10);
  // }


  private scrollToBottom(): void {
    if (!this.messagesContainer) return;

    // Forzar scroll al final de manera más agresiva
    this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight + 1000;

    // console.log('📍 Scroll posicionado al final:', {
    //   scrollTop: this.messagesContainer.scrollTop,
    //   scrollHeight: this.messagesContainer.scrollHeight
    // });
  }

  private setState(newState: Partial<WidgetState>): void {
    this.state = { ...this.state, ...newState };
  }


  private getButtonIcon(isOpen: boolean): string {
    return isOpen
      ? `

      <svg fill="currentColor"  viewBox="0 0 22 13" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M11 12.5934L0.40625 1.99963L2 0.405884L2.79688 1.20276L11 9.45276L20 0.452759L21.5938 1.99963L11 12.5934Z" fill="white"/>
</svg>
`
      : `
<svg viewBox="0 0 131 125" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M69.0574 0C34.9135 0 7.13604 28.0324 7.13604 62.5102C7.13604 66.3979 8.04844 71.145 8.94056 75.7489L9.06221 76.4037C9.95433 81.0075 10.6843 85.018 10.6843 87.6985C10.6843 87.7599 10.6031 89.0285 9.44745 92.5479C8.63643 95.0033 7.48072 98.0111 6.02089 101.449C4.6827 104.6 3.34452 107.485 2.47267 109.367C1.98606 110.39 1.58055 111.229 1.29669 111.823L0.911459 112.6C0.891184 112.662 0.850633 112.703 0.830357 112.764C-0.588928 115.71 -0.183418 119.25 1.84413 121.808C3.87168 124.366 7.21714 125.512 10.3801 124.754C10.4207 124.754 10.4612 124.734 10.5018 124.714C10.522 124.714 10.5423 124.714 10.5626 124.714L14.9218 123.629C17.6387 122.954 21.2883 122.074 25.0393 121.174C29.3985 120.13 32.9873 119.312 35.7042 118.739C37.2046 118.411 38.5022 118.166 39.5363 117.982C39.9824 117.9 40.327 117.838 40.6312 117.818H40.6717C41.1786 117.838 42.2127 117.982 44.1794 118.575C45.8622 119.087 47.6668 119.741 49.7754 120.478L50.9717 120.908C56.7502 122.974 62.8937 125 69.0371 125C103.181 125 130.979 96.9676 130.979 62.4898C130.979 28.012 103.201 0 69.0574 0ZM25.607 73.007C24.7757 68.751 24.0053 64.7201 24.0053 62.5102C24.0053 37.4448 44.2199 17.0445 69.0574 17.0445C93.8949 17.0445 114.11 37.4448 114.11 62.5102C114.11 87.5757 93.8949 107.976 69.0574 107.976C65.6106 107.976 61.0283 106.441 56.6488 104.866L55.5742 104.477C53.425 103.699 51.1744 102.901 49.0455 102.247C46.9368 101.612 43.7536 100.773 40.5501 100.773C39.3538 100.773 37.9954 100.978 36.6977 101.203C35.4609 101.428 33.9605 101.715 32.2979 102.083C29.8852 102.595 26.8438 103.29 23.2348 104.129C24.1269 101.858 24.8974 99.7913 25.5056 97.9293C26.9452 93.5914 27.6143 90.338 27.6143 87.6985C27.6143 83.1765 26.5802 77.836 25.6476 73.007H25.607Z" fill="white"/>
</svg>
      
      `;
    // : `
    // <svg viewBox="0 0 24 24" fill="currentColor">
    //   <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4v3c0 .6.4 1 1 1h.5c.2 0 .5-.1.7-.3L16.5 18H20c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
    // </svg>

    // `;
  }

  // Event system
  public on(event: WidgetEvent, callback: Function): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event)!.push(callback);
  }

  public off(event: WidgetEvent, callback: Function): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }


  visitorId: string = '';
  private emitEvent(event: WidgetEvent, payload?: any): void {
    const listeners = this.eventListeners.get(event);
    if (listeners && listeners.length > 0) {
      const eventData: WidgetEventData = {
        type: event,
        payload: {
          ...payload,
          visitorId: this.visitorId,
          conversationId: this.conversationId
        },
        timestamp: new Date()
      };

      listeners.forEach(callback => {
        try {
          callback(eventData);
        } catch (error) {
          console.error(`Error in event listener for ${event}:`, error);
        }
      });
    }

    // console.log(`🔔 Evento emitido: ${event}`, payload);
  }

  // Método público para obtener IDs
  // public getVisitorInfo(): { visitorId: string; conversationId: string } {
  //   return {
  //     visitorId: this.visitorId,
  //     conversationId: this.conversationId
  //   };
  // }
  public getVisitorInfo(): { clientUuid: string; conversationId: string; name: string | null; email: string | null } {
    return {
      clientUuid: this.clientUuid,
      conversationId: this.conversationId,
      name: this.visitorName,
      email: this.visitorEmail
    };
  }

  // Método público para limpiar datos (para testing)
  public clearStoredData(): void {
    try {
      localStorage.removeItem('chatio_visitor_id');
      localStorage.removeItem(`chatio_conversation_${this.visitorId}`);
      localStorage.removeItem(`chatio_messages_${this.conversationId}`);
      // console.log('🗑️ Datos del widget limpiados');
    } catch (error) {
      console.error('❌ Error limpiando datos:', error);
    }
  }

  // Public API
  public getState(): WidgetState {
    return { ...this.state };
  }

  public isOpen(): boolean {
    return this.state.isOpen;
  }

  public openWidget(): void {
    this.open();
  }

  public closeWidget(): void {
    this.close();
    this.animateButtonIcon(false);
  }

  public destroy(): void {

    if (this.markReadTimeout) {
      clearTimeout(this.markReadTimeout);
    }

    if (this.ablyService) {
      this.ablyService.disconnect();
    }

    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }

    if (this.messagesContainer) {
      this.messagesContainer.removeEventListener('scroll', this.handleScroll);
    }

    this.eventListeners.clear();
    this.emitEvent('widget:destroyed');
  }



  // probando crear fingerPrint
  // Generar ID único basado en dispositivo/navegador
  private generateUniqueVisitorId(): string {
    try {
      // Crear fingerprint básico del navegador
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx!.textBaseline = 'top';
      ctx!.font = '14px Arial';
      ctx!.fillText('Fingerprint', 2, 2);

      const fingerprint = [
        navigator.userAgent,
        navigator.language,
        screen.width + 'x' + screen.height,
        screen.colorDepth,
        new Date().getTimezoneOffset(),
        canvas.toDataURL()
      ].join('|');

      // Generar hash simple
      let hash = 0;
      for (let i = 0; i < fingerprint.length; i++) {
        const char = fingerprint.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
      }

      return `visitor_${Math.abs(hash)}_${Date.now()}`;
    } catch (error) {
      // Fallback si hay error
      return `visitor_${Math.random().toString(36).substr(2, 9)}_${Date.now()}`;
    }
  }


  //create fingerPrint deterministic
  private generateDeterministicFingerprint(): string {
    try {
      // Crear canvas fingerprint (más estable)
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx!.textBaseline = 'top';
      ctx!.font = '14px Arial';
      ctx!.fillStyle = '#f60';
      ctx!.fillRect(125, 1, 62, 20);
      ctx!.fillStyle = '#069';
      ctx!.fillText('Browser fingerprint', 2, 15);
      ctx!.fillStyle = 'rgba(102, 204, 0, 0.7)';
      ctx!.fillText('Browser fingerprint', 4, 17);
      const canvasFingerprint = canvas.toDataURL();

      // Obtener información WebGL (si está disponible)
      let webglFingerprint = '';
      try {
        const gl = canvas.getContext('webgl') as WebGLRenderingContext || canvas.getContext('experimental-webgl') as WebGLRenderingContext;
        if (gl) {
          const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
          webglFingerprint = gl.getParameter(debugInfo?.UNMASKED_RENDERER_WEBGL ?? gl.RENDERER);
        }
      } catch (e) {
        // WebGL no disponible
      }

      // Componentes estables del fingerprint
      const components = [
        navigator.userAgent,
        navigator.language,
        navigator.languages ? navigator.languages.join(',') : '',
        screen.width + 'x' + screen.height,
        screen.colorDepth,
        screen.pixelDepth,
        new Date().getTimezoneOffset(),
        navigator.platform,
        navigator.hardwareConcurrency || 0,
        (navigator as any).deviceMemory || 0,
        navigator.maxTouchPoints || 0,
        canvasFingerprint,
        webglFingerprint,
        // Características del navegador
        typeof window.indexedDB !== 'undefined',
        typeof window.sessionStorage !== 'undefined',
        typeof window.localStorage !== 'undefined',
        'openDatabase' in window
      ];

      // Crear string estable
      const fingerprintString = components.join('|');

      // Generar hash determinístico
      let hash = 0;
      for (let i = 0; i < fingerprintString.length; i++) {
        const char = fingerprintString.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convertir a entero de 32 bits
      }

      // Crear ID más legible y estable
      const baseId = Math.abs(hash).toString(36);
      return `visitor_${baseId}`;

    } catch (error) {
      console.error('Error generando fingerprint:', error);
      // Fallback más simple pero estable
      return this.generateSimpleFingerprint();
    }
  }

  private generateSimpleFingerprint(): string {
    // Fallback usando solo información básica del navegador
    const basicInfo = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      screen.colorDepth,
      new Date().getTimezoneOffset(),
      navigator.platform
    ].join('|');

    let hash = 0;
    for (let i = 0; i < basicInfo.length; i++) {
      const char = basicInfo.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }

    return `visitor_${Math.abs(hash).toString(36)}`;
  }



  private createNameForm(): void {
    const existingWindow = this.container.querySelector('.webchat-window');
    if (existingWindow) {
      existingWindow.remove();
    }

    const formWindow = document.createElement('div');

    formWindow.className = 'webchat-window registration-form';
    formWindow.innerHTML = `
    <div class="webchat-header">
        <div class="webchat-header-info">
          <div class="webchat-title">Iniciar Chat</div>
        </div>
        <button class="webchat-close-btn" aria-label="Cerrar chat">×</button>
      </div>
      <div class="webchat-form-container">
 
      <div class="webchat-welcome">
  <div class="welcome-icon">👋</div>
  <h3>¡Hola! Bienvenido...</h3>
  <p>Para brindarte una mejor atención personalizada, necesitamos algunos datos.</p>
</div>

<!-- Ola decorativa separadora -->
<div class="webchat-wave">
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" preserveAspectRatio="none">
    <path fill="currentColor" fill-opacity="1" d="M0,224L60,192C120,160,240,96,360,69.3C480,43,600,53,720,85.3C840,117,960,171,1080,165.3C1200,160,1320,96,1380,64L1440,32L1440,0L1380,0C1320,0,1200,0,1080,0C960,0,840,0,720,0C600,0,480,0,360,0C240,0,120,0,60,0L0,0Z"></path>
  </svg>
</div>

        <div class="webchat-form">
          <div class="form-group">
            <label for="visitor-name-input">Tu nombre *</label>
            <input 
              type="text" 
              id="visitor-name-input" 
              class="webchat-name-input" 
              placeholder="Ej: Juan Pérez" 
              maxlength="100"
              required
            >
          </div>
          <div class="form-group">
            <label for="visitor-email-input">Tu correo electrónico *</label>
            <input 
              type="email" 
              id="visitor-email-input" 
              class="webchat-email-input" 
              placeholder="ejemplo@correo.com" 
              maxlength="100"
              required
            >
            <small class="form-hint">Usaremos tu email para recuperar tus conversaciones</small>
          </div>
          <button class="webchat-start-btn" id="start-chat-btn" disabled>
            Iniciar Chat
          </button>
          <div class="error-message-container" id="error-container"></div>
        </div>
       <div class="webchat-privacy">
  <small>
    <svg class="lock-icon" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
      <path fill-rule="evenodd" d="M8 10V7a4 4 0 1 1 8 0v3h1a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h1Zm2-3a2 2 0 1 1 4 0v3h-4V7Zm2 6a1 1 0 0 1 1 1v3a1 1 0 1 1-2 0v-3a1 1 0 0 1 1-1Z" clip-rule="evenodd"/>
    </svg>
    Tu información está protegida y no será compartida con terceros.
  </small>
</div>

      </div>
    `;

    this.container.appendChild(formWindow);
    this.setupNameFormListeners(formWindow);
  }

  // Event listeners para el formulario de nombre
  private setupNameFormListeners(formWindow: HTMLElement): void {
    // const nameInput = formWindow.querySelector('#visitor-name-input') as HTMLInputElement;
    // const startBtn = formWindow.querySelector('#start-chat-btn') as HTMLButtonElement;
    // const closeBtn = formWindow.querySelector('.webchat-close-btn') as HTMLButtonElement;
    const nameInput = formWindow.querySelector('#visitor-name-input') as HTMLInputElement;
    const emailInput = formWindow.querySelector('#visitor-email-input') as HTMLInputElement;
    const startBtn = formWindow.querySelector('#start-chat-btn') as HTMLButtonElement;
    const closeBtn = formWindow.querySelector('.webchat-close-btn') as HTMLButtonElement;
    const errorContainer = formWindow.querySelector('#error-container') as HTMLElement;

    // Validación en tiempo real
    // nameInput.addEventListener('input', () => {
    //   const name = nameInput.value.trim();
    //   startBtn.disabled = name.length < 2;

    //   // Remover clases de error
    //   nameInput.classList.remove('error');
    // });
    // Validación en tiempo real
    const validateForm = () => {
      const name = nameInput.value.trim();
      const email = emailInput.value.trim();
      const isValidEmail = this.isValidEmail(email);

      startBtn.disabled = !(name.length >= 2 && isValidEmail);

      // Remover clases de error
      nameInput.classList.remove('error');
      emailInput.classList.remove('error');
      errorContainer.innerHTML = '';
    };

    nameInput.addEventListener('input', validateForm);
    emailInput.addEventListener('input', validateForm);
    // Enter para enviar
    // nameInput.addEventListener('keydown', (e) => {
    //   if (e.key === 'Enter' && !startBtn.disabled) {
    //     e.preventDefault();
    //     this.handleNameSubmit(nameInput.value.trim());
    //   }
    // });

    // Enter para enviar
    const handleEnter = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !startBtn.disabled) {
        e.preventDefault();
        this.handleRegistrationSubmit(nameInput.value.trim(), emailInput.value.trim());
      }
    };

    nameInput.addEventListener('keydown', handleEnter);
    emailInput.addEventListener('keydown', handleEnter);

    // Botón iniciar chat
    // startBtn.addEventListener('click', () => {
    //   const name = nameInput.value.trim();
    //   if (name.length >= 2) {
    //     this.handleNameSubmit(name);
    //   }
    // });

    // Botón iniciar chat
    startBtn.addEventListener('click', () => {
      const name = nameInput.value.trim();
      const email = emailInput.value.trim();
      if (name.length >= 2 && this.isValidEmail(email)) {
        this.handleRegistrationSubmit(name, email);
      }
    });

    // Botón cerrar
    closeBtn.addEventListener('click', () => {
      this.close();
      this.animateButtonIcon(false);
    });

    // Auto-focus en el input
    setTimeout(() => nameInput.focus(), 100);
  }

  // Validar formato de email
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // Manejar envío de registro
  private async handleRegistrationSubmit(name: string, email: string): Promise<void> {
    if (name.length < 2) {
      this.showRegistrationError('El nombre debe tener al menos 2 caracteres');
      return;
    }

    if (!this.isValidEmail(email)) {
      this.showRegistrationError('Por favor, ingresa un correo electrónico válido');
      return;
    }

    // console.log('📝 Registrando cliente:', { name, email });

    // Deshabilitar botón mientras se procesa
    const startBtn = this.container.querySelector('#start-chat-btn') as HTMLButtonElement;
    if (startBtn) {
      startBtn.disabled = true;
      startBtn.textContent = 'Verificando...';
    }

    try {
      // Intentar validar cliente
      const result = await this.apiService.validateWebClient(name, email);

      // Cliente nuevo o validado correctamente
      this.saveClientData(name, email, result.clientUuid);

      // Crear ventana de chat
      this.createChatWindow();
      await this.loadConversationHistory();

      // if (!this.ablyService && this.config.ablyApiKey) {
      //   await this.initializeAbly();
      // }
      if (this.config.ablyApiKey && !this.ablyService) {
        // console.log('🔄 Inicializando Ably para nuevo cliente...');
        await this.initializeAbly();
      }

      this.emitEvent('visitor:identified', {
        visitorName: this.visitorName,
        visitorEmail: this.visitorEmail,
        clientUuid: this.clientUuid,
        conversationId: this.conversationId
      });

    } catch (error: any) {
      console.error('❌ Error en registro:', error);
      // console.log('🔍 Verificando tipo de error:', {
      //   hasNeedsOTP: !!error.needsOTP,
      //   errorMessage: error.message
      // });

      if (error.needsOTP === true) {
        // Email ya existe, mostrar formulario OTP
        this.createOTPForm(email);
      } else {
        this.showRegistrationError('Error al registrar. Intenta de nuevo.');
        if (startBtn) {
          startBtn.disabled = false;
          startBtn.textContent = 'Iniciar Chat';
        }
      }
    }
  }

  // Mostrar error en formulario de registro
  private showRegistrationError(message: string): void {
    const errorContainer = this.container.querySelector('#error-container');
    if (errorContainer) {
      errorContainer.innerHTML = `
        <div class="error-message">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          ${message}
        </div>
      `;
    }
  }

  // NUEVO: Crear formulario OTP
  private createOTPForm(email: string): void {
    const existingWindow = this.container.querySelector('.webchat-window');
    if (existingWindow) {
      existingWindow.remove();
    }

    const otpWindow = document.createElement('div');
    otpWindow.className = 'webchat-window otp-form';
    otpWindow.innerHTML = `
      <div class="webchat-header">
        <div class="webchat-header-info">
          <div class="webchat-title">Verificación de Email</div>
          <div class="webchat-subtitle">Recuperar acceso</div>
        </div>
        <button class="webchat-close-btn" aria-label="Cerrar chat">×</button>
      </div>
  
      <div class="webchat-form-container">
        <div class="webchat-welcome">
          <div class="welcome-icon">
            <svg  aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
  <path d="M2.038 5.61A2.01 2.01 0 0 0 2 6v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6c0-.12-.01-.238-.03-.352l-.866.65-7.89 6.032a2 2 0 0 1-2.429 0L2.884 6.288l-.846-.677Z"/>
  <path d="M20.677 4.117A1.996 1.996 0 0 0 20 4H4c-.225 0-.44.037-.642.105l.758.607L12 10.742 19.9 4.7l.777-.583Z"/>
</svg>

          </div>
          <h3>¡Ya tienes una cuenta!</h3>
          <p>Hemos enviado un código de verificación a:</p>
          <p class="email-display">${email}</p>
          <p>Ingresa el código para recuperar tus conversaciones.</p>
        </div>


            <div class="webchat-wave">
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" preserveAspectRatio="none">
    <path fill="currentColor" fill-opacity="1" d="M0,224L60,192C120,160,240,96,360,69.3C480,43,600,53,720,85.3C840,117,960,171,1080,165.3C1200,160,1320,96,1380,64L1440,32L1440,0L1380,0C1320,0,1200,0,1080,0C960,0,840,0,720,0C600,0,480,0,360,0C240,0,120,0,60,0L0,0Z"></path>
  </svg>
</div>

        <div class="webchat-form">
          <div class="form-group">
            <label for="otp-code-input">Código de verificación *</label>
            <input 
              type="text" 
              id="otp-code-input" 
              class="webchat-otp-input" 
              placeholder="000000" 
              maxlength="6"
              pattern="[0-9]*"
              inputmode="numeric"
              required
              autocomplete="one-time-code"
            >
            <small class="form-hint">El código es válido por 10 minutos</small>
          </div>
          <button class="webchat-verify-btn" id="verify-otp-btn" disabled>
            Verificar Código
          </button>
          <button class="webchat-resend-btn" id="resend-otp-btn">
            Reenviar código
          </button>
          <button class="webchat-back-btn" id="back-to-registration-btn">
            ← Volver al registro
          </button>
          <div class="error-message-container" id="otp-error-container"></div>
        </div>
      </div>
    `;

    this.container.appendChild(otpWindow);
    this.setupOTPFormListeners(otpWindow, email);
  }

  // Event listeners para formulario OTP
  private setupOTPFormListeners(otpWindow: HTMLElement, email: string): void {
    const otpInput = otpWindow.querySelector('#otp-code-input') as HTMLInputElement;
    const verifyBtn = otpWindow.querySelector('#verify-otp-btn') as HTMLButtonElement;
    const resendBtn = otpWindow.querySelector('#resend-otp-btn') as HTMLButtonElement;
    const backBtn = otpWindow.querySelector('#back-to-registration-btn') as HTMLButtonElement;
    const closeBtn = otpWindow.querySelector('.webchat-close-btn') as HTMLButtonElement;

    // Validación en tiempo real
    otpInput.addEventListener('input', () => {
      // Solo permitir números
      otpInput.value = otpInput.value.replace(/[^0-9]/g, '');

      const code = otpInput.value.trim();
      verifyBtn.disabled = code.length !== 6;

      otpInput.classList.remove('error');
      const errorContainer = otpWindow.querySelector('#otp-error-container');
      if (errorContainer) errorContainer.innerHTML = '';
    });

    // Enter para verificar
    otpInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !verifyBtn.disabled) {
        e.preventDefault();
        this.handleOTPVerification(email, otpInput.value.trim());
      }
    });

    // Botón verificar
    verifyBtn.addEventListener('click', () => {
      this.handleOTPVerification(email, otpInput.value.trim());
    });

    // Botón reenviar
    resendBtn.addEventListener('click', async () => {
      resendBtn.disabled = true;
      resendBtn.textContent = 'Reenviando...';

      try {
        await this.apiService.resendOTP(email);
        this.showOTPSuccess('Código reenviado correctamente');

        // Reactivar botón después de 30 segundos
        setTimeout(() => {
          resendBtn.disabled = false;
          resendBtn.textContent = 'Reenviar código';
        }, 30000);
      } catch (error) {
        this.showOTPError('Error reenviando código. Intenta de nuevo.');
        resendBtn.disabled = false;
        resendBtn.textContent = 'Reenviar código';
      }
    });

    // Botón volver
    backBtn.addEventListener('click', () => {
      this.createNameForm();
    });

    // Botón cerrar
    closeBtn.addEventListener('click', () => this.close());

    // Auto-focus en input OTP
    setTimeout(() => otpInput.focus(), 100);
  }

  // Manejar verificación OTP
  private async handleOTPVerification(email: string, otpCode: string): Promise<void> {
    // console.log('🔐 Verificando código OTP');

    const verifyBtn = this.container.querySelector('#verify-otp-btn') as HTMLButtonElement;
    if (verifyBtn) {
      verifyBtn.disabled = true;
      verifyBtn.textContent = 'Verificando...';
    }

    try {
      const result = await this.apiService.verifyOTP(email, otpCode);

      // OTP verificado, obtener nombre del localStorage o pedir de nuevo
      const storedName = localStorage.getItem('chatio_visitor_name') || 'Usuario';

      this.saveClientData(storedName, email, result.clientUuid);

      // Crear ventana de chat
      this.createChatWindow();
      await this.loadConversationHistory();

      if (!this.ablyService && this.config.ablyApiKey) {
        await this.initializeAbly();
      }

      this.emitEvent('visitor:identified', {
        visitorName: this.visitorName,
        visitorEmail: this.visitorEmail,
        clientUuid: this.clientUuid,
        conversationId: this.conversationId
      });

    } catch (error) {
      console.error('❌ Error verificando OTP:', error);
      this.showOTPError('Código incorrecto o expirado. Intenta de nuevo.');

      if (verifyBtn) {
        verifyBtn.disabled = false;
        verifyBtn.textContent = 'Verificar Código';
      }
    }
  }


  // Mostrar error en formulario OTP
  private showOTPError(message: string): void {
    const errorContainer = this.container.querySelector('#otp-error-container');
    const otpInput = this.container.querySelector('#otp-code-input') as HTMLInputElement;

    if (otpInput) otpInput.classList.add('error');

    if (errorContainer) {
      errorContainer.innerHTML = `
        <div class="error-message">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          ${message}
        </div>
      `;
    }
  }

  // Mostrar mensaje de éxito en formulario OTP
  private showOTPSuccess(message: string): void {
    const errorContainer = this.container.querySelector('#otp-error-container');

    if (errorContainer) {
      errorContainer.innerHTML = `
        <div class="success-message">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          ${message}
        </div>
      `;
    }
  }




  // Manejar envío de nombre
  private async handleNameSubmit(name: string): Promise<void> {
    if (name.length < 2) {
      this.showNameError('El nombre debe tener al menos 2 caracteres');
      return;
    }

    // Validar caracteres básicos
    if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(name)) {
      this.showNameError('Por favor, usa solo letras para tu nombre');
      return;
    }

    // console.log('📝 Guardando nombre de visitante:', name);
    // console.log('🆔 Visitor ID:', this.visitorId);

    try {
      // Guardar nombre
      this.visitorName = name;

      // Guardar en localStorage para futuras visitas
      localStorage.setItem('chatio_visitor_name', name);

      // Actualizar conversationId basado en visitor ID
      this.conversationId = `conv_${this.visitorId}`;

      // Crear ventana de chat
      this.createChatWindow();

      // Cargar historial de conversación
      await this.loadConversationHistory();

      // Inicializar servicios si es necesario
      if (!this.ablyService) {
        await this.initializeAbly();
      }

      this.emitEvent('visitor:identified', {
        visitorName: this.visitorName,
        visitorId: this.visitorId,
        conversationId: this.conversationId
      });

    } catch (error) {
      console.error('❌ Error guardando información del visitante:', error);
      this.showNameError('Error guardando información. Intenta de nuevo.');
    }
  }
  // Mostrar error en formulario de nombre
  private showNameError(message: string): void {
    const nameInput = this.container.querySelector('#visitor-name-input') as HTMLInputElement;
    const existingError = this.container.querySelector('.error-message');

    if (existingError) {
      existingError.remove();
    }

    nameInput.classList.add('error');

    const errorDiv = document.createElement('div');
    errorDiv.className = 'error-message';
    errorDiv.textContent = message;

    nameInput.parentNode!.appendChild(errorDiv);
    nameInput.focus();
  }

  // Método público para limpiar datos (útil para testing o reset)
  public clearVisitorData(): void {
    try {
      // localStorage.removeItem('chatio_visitor_id');
      // localStorage.removeItem('chatio_visitor_name');
      // localStorage.removeItem(`chatio_messages_${this.conversationId}`);
      localStorage.removeItem('chatio_visitor_name');
      localStorage.removeItem('chatio_visitor_email'); // Nueva propiedad
      localStorage.removeItem('chatio_client_uuid');   // Nueva propiedad
      localStorage.removeItem('chatio_api_key');

      this.visitorName = null;
      this.visitorEmail = null;
      this.clientUuid = '';
      this.conversationId = '';
      // this.visitorId = this.getVisitorId(); // Generar nuevo ID
      // this.conversationId = '';

      // console.log('🗑️ Datos del visitante limpiados');
    } catch (error) {
      console.error('❌ Error limpiando datos:', error);
    }
  }

  // public debugAbly(): void {
  //   console.log('🔍 Debug Ably:', {
  //     visitorId: this.visitorId,
  //     hasAblyService: !!this.ablyService,
  //     isConnected: this.state.isConnected,
  //     channelInfo: this.ablyService ? this.ablyService.getChannelInfo() : 'No inicializado'
  //   });

  //   if (this.ablyService) {
  //     console.log('📊 Info detallada del canal:', this.ablyService.getChannelInfo());
  //   }
  // }



  private unreadCount: number = 0;
  private checkUnreadInterval?: number;

  /**
   * Verificar mensajes no leídos
   */
  private async checkUnreadMessages(): Promise<void> {
    try {
      const count = await this.apiService.getUnreadMessagesCount(this.clientUuid);

      if (count > 0 && count !== this.unreadCount) {
        this.unreadCount = count;
        this.showUnreadBadge(count);
        this.emitEvent('messages:unread', { count });
        // console.log(`📬 ${count} mensaje(s) no leído(s)`);
      } else if (count === 0 && this.unreadCount > 0) {
        this.unreadCount = 0;
        this.hideUnreadBadge();
      }

    } catch (error) {
      console.error('❌ Error verificando mensajes no leídos:', error);
    }
  }

  /**
  * Iniciar polling de mensajes no leídos
  */
  private startUnreadMessagesPolling(): void {
    // Verificar cada 30 segundos
    this.checkUnreadInterval = window.setInterval(() => {
      // Solo verificar si el widget está cerrado
      if (!this.state.isOpen) {
        this.checkUnreadMessages();
      }
    }, 30000); // 30 segundos

    console.log('⏰ Polling de mensajes no leídos iniciado');
  }

  /**
   * Detener polling de mensajes no leídos
   */
  private stopUnreadMessagesPolling(): void {
    if (this.checkUnreadInterval) {
      clearInterval(this.checkUnreadInterval);
      this.checkUnreadInterval = undefined;
      console.log('⏰ Polling de mensajes no leídos detenido');
    }
  }

  /**
   * Mostrar badge de mensajes no leídos
   */
  private showUnreadBadge(count: number): void {
    const button = this.container.querySelector('.webchat-toggle-btn');
    if (!button) return;

    // Agregar clase de notificación
    button.classList.add('has-notification');

    // Crear o actualizar badge
    let badge = button.querySelector('.notification-badge') as HTMLElement;
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'notification-badge';
      button.appendChild(badge);
    }

    // Mostrar número si es menor a 10, sino mostrar "9+"
    badge.textContent = count > 9 ? '9+' : count.toString();

    // Animación de entrada
    badge.style.animation = 'none';
    setTimeout(() => {
      badge.style.animation = 'bounce 0.6s ease-in-out';
    }, 10);

    // console.log(`🔔 Badge mostrado con ${count} mensaje(s)`);
  }

  /**
   * Ocultar badge de mensajes no leídos
   */
  private hideUnreadBadge(): void {
    const button = this.container.querySelector('.webchat-toggle-btn');
    if (!button) return;

    button.classList.remove('has-notification');
    const badge = button.querySelector('.notification-badge');
    if (badge) {
      badge.remove();
    }

    // console.log('🔕 Badge ocultado');
  }


  private markReadTimeout?: number;

  /**
   * Marcar mensajes como leídos
   */
  private async markMessagesAsRead_quitar(): Promise<void> {
    try {
      await this.apiService.markMessagesAsRead(this.clientUuid);
      this.unreadCount = 0;
      // console.log('✅ Mensajes marcados como leídos');
    } catch (error) {
      console.error('❌ Error marcando mensajes como leídos:', error);
    }
  }
  private async markMessagesAsRead(): Promise<void> {
    if (this.markReadTimeout) {
      clearTimeout(this.markReadTimeout);
    }

    // Esperar 500ms antes de marcar como leído
    // Esto agrupa múltiples mensajes que lleguen rápido
    this.markReadTimeout = window.setTimeout(async () => {
      try {
        await this.apiService.markMessagesAsRead(this.clientUuid);
        this.unreadCount = 0;
        // console.log('✅ Mensajes marcados como leídos (desde Ably)');
      } catch (error) {
        console.error('❌ Error marcando mensajes como leídos:', error);
      }
    }, 500);
  }





}
